<template>
  <div class="aspect-video w-full video-container">
    <video
      :id="videoContentEl"
      ref="videoElement"
      muted
      playsinline
      class="entry-video-item video-js w-full h-full vjs-fluid vjs-16-9 relative"
    ></video>
    <div
      class="
        hidden
        close-button
        h-7
        w-16
        p-2
        items-center
        justify-center
        text-13
        absolute
        -top-7
        left-0
        cursor-pointer
        bg-black
        text-white
        z-75
        rounded-t-lg
      "
    >
      {{ $t('main.dialog.close') }}
    </div>
  </div>
</template>

<script>
import { defineComponent, inject, useMeta, ref, ssrRef, watch, useRoute, useContext, onMounted, useFetch } from '@nuxtjs/composition-api';
import { uniqueId, shuffle } from 'lodash';
// import { playerOptions } from '../constants/playerOptions';
import { videoEvents } from '../utils/videoEvents';
import { DESKTOP_AD_UNIT, MOBILE_AD_UNIT } from '../constants/adUnit';
import { interSection } from '../utils/videoIntersection';
import { useDevice } from '~/composables/useDevice';
import { generateVideoSchema } from '~/modules/common/utils/schemaUtils';
import Recommendation from '~/modules/video/plugins/recommendation/plugin';
import { LANG, HOST_URL, SPONSORED } from '~/constants/config';

export default defineComponent({
  head: {},
  props: {
    entry: {
      type: Object,
      required: true,
    },
    isHero: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    let player;
    const route = useRoute();
    const recommendedVideos = ssrRef('');
    const videoContentEl = ref(null);
    const { $service } = useContext();
    const article = inject('article');
    const videoElement = ref(null);
    const innerHTML = JSON.stringify(generateVideoSchema(props.entry, article), null, 2);
    const hid = uniqueId('nuxt-jsonld-');
    const { isMobile } = useDevice();
    const isSponsored = SPONSORED.some((i) => (article?.value ?? article)?.tags?.includes(i));

    useMeta(() => ({
      script: [
        {
          hid,
          src: 'http://imasdk.googleapis.com/js/sdkloader/ima3.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'http://cdnjs.cloudflare.com/ajax/libs/video.js/8.5.2/video.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'http://cdnjs.cloudflare.com/ajax/libs/video.js/8.5.2/lang/tr.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'http://cdnjs.cloudflare.com/ajax/libs/videojs-contrib-ads/7.3.2/videojs-contrib-ads.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'http://cdnjs.cloudflare.com/ajax/libs/videojs-contrib-quality-levels/4.0.0/videojs-contrib-quality-levels.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          src: 'http://cdnjs.cloudflare.com/ajax/libs/videojs-ima/2.2.0/videojs.ima.min.js',
          type: 'text/javascript',
          body: true,
        },
        {
          hid,
          type: 'application/ld+json',
          innerHTML,
        },
      ],
      link: [
        {
          href: 'http://cdnjs.cloudflare.com/ajax/libs/video.js/8.5.2/video-js.min.css',
          rel: 'stylesheet',
          integrity: 'sha512-+CRWio9t8I0yMYqwHOQWIybOeIlfACzx/HkiJAvhhpPD0G310qhPkhh0xSnPhAH0hXijZoxUoTEEL5RzSyvOow==',
          crossorigin: 'anonymous',
          referrerpolicy: 'no-referrer',
        },
        {
          href: 'http://cdnjs.cloudflare.com/ajax/libs/videojs-ima/2.2.0/videojs.ima.min.css',
          rel: 'stylesheet',
          integrity: 'sha512-hw6cZAVUjqC0sNVfGtFdnmgTsNLElCJIL4Ek0QPfEQUSMTuOhVkBjEW4nrN++PFeZVh3j1b2oenyoG3b8VQaag==',
          crossorigin: 'anonymous',
          referrerpolicy: 'no-referrer',
        },
        {
          href: '/css/custom.video.css',
          rel: 'stylesheet',
          preload: true,
        },
      ],
      __dangerouslyDisableSanitizersByTagID: {
        [hid]: ['innerHTML'],
      },
    }));

    useFetch(async () => {
      if (process.client) return;
      const data = await $service.article.getRecommendedArticle(article.legacyId);
      recommendedVideos.value = shuffle(data).slice(0, 3) || null;
    });

    // TODO FOR COMING SPRINT
    // const embedLink = `${HOST_URL}/player/${article.legacyId}/${props.entry._id}`;

    // TODO FOR COMING SPRINT
    // const shareOptions = {
    //   socials: ['fb'],
    //   url: `${HOST_URL}${route.value.path}`,
    //   title: 'Onedio Video',
    //   description: i18n.t('share-button.share-video'),
    //   fbAppId: null,
    //   embedCode: "<iframe src='" + embedLink + "'  width='560' height='315' frameborder='0' allowfullscreen></iframe>",
    // };

    const adParameters = () => {
      const categoriesId = article.categories.map((category) => category._id);
      if (categoriesId.length) {
        // INFO: &etiket=prerolltest (local üzerinde reklamları oynatmak için)
        return encodeURIComponent(`kategori=${categoriesId.toString()}`);
      }
    };

    if (props.isHero) {
      videoContentEl.value = 'video_hero';
    } else {
      videoContentEl.value = 'video_' + props.entry.videoData.id;
    }

    onMounted(() => {
      if (!window.videojs) return;
      const playerOptions = {
        muted: true,
        debug: false,
        autoplay: true,
        controls: true,
        preload: 'auto',
        errorDisplay: false,
        controlBar: {
          volumePanel: {
            inline: true,
          },
          pictureInPictureToggle: false,
        },
        language: LANG,
        html5: {
          nativeVideoTracks: true,
          nativeAudioTracks: true,
          hls: {
            overrideNative: !videojs.browser.IS_ANY_SAFARI, // eslint-disable-line no-undef
            enableWorker: true,
          },
          vhs: {
            activeLowInitialPlaylist: true,
            limitRenditionByPlayerDimensions: true,
            overrideNative: !videojs.browser.IS_ANY_SAFARI, // eslint-disable-line no-undef
            enableWorker: true,
          },
        },
      };

      // eslint-disable-next-line no-undef
      player = videojs(videoElement.value, {
        ...playerOptions,
        sources: [
          {
            type: 'application/x-mpegURL',
            src: props.entry?.videoData?.url,
          },
        ],
      });

      let startEvent = 'click';
      if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/Android/i)) {
        startEvent = 'touchend';
      }

      const adTagUrl = `http://pubads.g.doubleclick.net/gampad/ads?iu=/21814681/${
        isMobile.value ? MOBILE_AD_UNIT : DESKTOP_AD_UNIT
      }&description_url=${encodeURI(
        HOST_URL + route.value.path
      )}&tfcd=0&npa=0&sz=640x480&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&plcmt=1&vpa=auto&vpmute=1&wta=1&vpos=preroll&impl=s&correlator=${Date.now()}&cust_params=${adParameters()}`;

      const initAdDisplayContainer = function () {
        player.ima.initializeAdDisplayContainer();
        wrapperDiv.removeEventListener(startEvent, initAdDisplayContainer);
      };
      const wrapperDiv = document.getElementById(videoContentEl.value);
      if (player && player.ima && !isSponsored && props.entry.videoData.duration < 30) {
        try {
          player.ima({ id: `video_${props.entry.videoData.id}`, adTagUrl, locale: LANG });
          wrapperDiv.addEventListener(startEvent, initAdDisplayContainer);
        } catch (error) {}
      }

      // calculate player height for 16/9 ratio
      if (isMobile.value) {
        const playerWidth = window.innerWidth - 32;
        const playerHeight = (playerWidth * 9) / 16;
        wrapperDiv.style.height = playerHeight + 'px';
      } else {
        const playerWidth = wrapperDiv.style.width;
        const playerHeight = (playerWidth * 9) / 16;
        wrapperDiv.style.height = playerHeight + 'px';
      }

      player.ready(() => {
        videoEvents({ player, route });
        props.isHero && sendCurrentPlayingId();
        if (recommendedVideos.value) {
          new Recommendation(player, recommendedVideos.value, true); // eslint-disable-line no-new
        }

        // videojs.addLanguage(LANG, {
        //   Share: i18n.t('main.share'),
        //   'Direct Link': i18n.t('video-player.direct-link'),
        //   'Embed Code': i18n.t('video-player.embed-code'),
        //   Copy: i18n.t('main.copy'),
        //   Copied: i18n.t('main.copied'),
        // });

        const videoCount = article?.entries.reduce((acc, entry) => {
          if (entry.videoData?.source === 'onedio') {
            acc++;
          }
          return acc;
        }, 0);

        // player.hlsQualitySelector();
        player.poster(props.entry.image?.resolutions.standard.url || props.entry?.thumbnailUrl);

        interSection(wrapperDiv.parentElement, player, videoCount, isMobile.value, props.isHero);
      });

      // let qualityLevels = player.qualityLevels();

      // // disable quality levels with less than 720 horizontal lines of resolution when added
      // // to the list.
      // qualityLevels.on('addqualitylevel', function(event) {
      //   let qualityLevel = event.qualityLevel;

      //   if (qualityLevel.height >= 720) {
      //     qualityLevel.enabled = true;
      //   } else {
      //     qualityLevel.enabled = false;
      //   }
      // });

      // // example function that will toggle quality levels between SD and HD, defining and HD
      // // quality as having 720 horizontal lines of resolution or more
      // let toggleQuality = (function() {
      //   let enable720 = true;

      //   return function() {
      //     for (let qualityLevel of qualityLevels) {
      //       if (qualityLevel.height >= 720) {
      //         qualityLevel.enabled = enable720;
      //       } else {
      //         qualityLevel.enabled = !enable720;
      //       }
      //     }
      //     enable720 = !enable720;
      //   };
      // })();

      // let currentSelectedQualityLevelIndex = qualityLevels.selectedIndex; // -1 if no level selected

      // // Listen to change events for when the player selects a new quality level
      // qualityLevels.on('change', function() {
      //   console.log('Quality Level changed!');
      //   console.log('New level:', qualityLevels[qualityLevels.selectedIndex]);
      // });
    });

    const sendCurrentPlayingId = () => {
      player.on('playing', function () {
        emit('current-video-id', props.entry?.videoData?.id);
      });
    };

    watch(
      () => props.entry,
      () => {
        try {
          player.ima.requestAds();
        } catch (error) {}
        player.src({ type: 'application/x-mpegurl', src: props.entry.videoData.url });
        player.ready(() => {
          player.play();
          sendCurrentPlayingId();
        });
      },
      { deep: true }
    );
    return { videoElement, videoContentEl };
  },
});
</script>

<style>
.entry-video-item {
  overflow: hidden !important;
}
.stick-video {
  position: fixed !important;
  top: auto;
  bottom: 20px;
  left: 20px;
  width: 322px !important;
  height: 181px !important;
  animation: fade-in-up 0.25s ease;
  z-index: 100;
}

@keyframes fade-in-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.ima-ad-container {
  z-index: 2 !important;
}

.video-js .vjs-play-progress:before {
  top: 0px !important;
}
</style>
